// src/pages/MarketingManagement.js - Super Admin Marketing Content Dashboard
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import SubscriptionPlans from "../components/billing/SubscriptionPlans";
import FAQManagement from "../components/billing/FAQManagement";
import { apiService } from "../services/api";
import toast from "react-hot-toast";
import {
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";

const MarketingManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("plans");
  const [loading, setLoading] = useState(true);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    if (user?.role === "super_admin") {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [plansData, faqsData] = await Promise.all([
        apiService.getSubscriptionPlans(),
        apiService.getFaqs(),
      ]);
      setSubscriptionPlans(plansData);
      setFaqs(faqsData || []);
    } catch (error) {
      console.error("Error loading marketing data:", error);
      toast.error("Failed to load marketing data");
    }
    setLoading(false);
  };

  const getTabContent = () => {
    switch (activeTab) {
      case "faqs":
        return <FAQManagement faqs={faqs} onUpdate={loadData} />;
      default:
        return (
          <SubscriptionPlans plans={subscriptionPlans} onUpdate={loadData} />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  if (user?.role !== "super_admin") {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-lg font-medium text-gray-900">
          Access Denied
        </h3>
        <p className="mt-1 text-gray-500">
          Only super administrators can access marketing management.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Marketing Management
        </h1>
        <p className="text-gray-600 mt-1">
          Manage public pricing plans and FAQs displayed on the marketing site
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "plans", name: "Plans", icon: Cog6ToothIcon },
            { id: "faqs", name: "FAQs", icon: QuestionMarkCircleIcon },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === tab.id
                      ? "border-slate-500 text-slate-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }
                `}
              >
                <Icon
                  className={`
                  -ml-0.5 mr-2 h-5 w-5
                  ${
                    activeTab === tab.id
                      ? "text-slate-500"
                      : "text-gray-400 group-hover:text-gray-500"
                  }
                `}
                />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">{getTabContent()}</div>
    </div>
  );
};

export default MarketingManagement;
